import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Sparkles, X, Check, LoaderCircle, ImagePlus } from 'lucide-react';
import { MBtn } from './adminUI';
import { pollAiJob, fmtElapsed } from './aiJob';
import { cn } from '@/lib/utils';

/* AI image-generator button for every image field (page/header/footer/product/blog).
 * Opens a modal: prompt + style presets + ratio → bot queue → preview → use. No coding needed.
 * `suggested`: recommended ratio for this spot (preselects + shows a hint). */
const RATIO_LABELS = { '1:1': 'مربع', '16:9': 'افقی', '9:16': 'عمودی', '4:3': 'کلاسیک', '3:4': 'پرتره' };
const STYLE_PRESETS = [
  { id: 'studio', label: 'نور استودیویی', suffix: 'نور استودیویی حرفه‌ای' },
  { id: 'real', label: 'واقع‌گرایانه', suffix: 'واقع‌گرایانه با جزئیات بالا' },
  { id: 'minimal', label: 'مینیمال', suffix: 'پس‌زمینه ساده و مینیمال' },
  { id: 'notext', label: 'بدون نوشته', suffix: 'بدون هیچ متن و نوشته' },
];

export function AiImageButton({ onPick, resolveSrc, suggested }) {
  const validSug = RATIO_LABELS[suggested] ? suggested : null;
  const cancelRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [styles, setStyles] = useState(['studio']);
  const [ratio, setRatio] = useState(validSug || '1:1');
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [err, setErr] = useState('');
  const [img, setImg] = useState('');

  const toggleStyle = id => setStyles(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const fullPrompt = () => {
    const base = prompt.trim();
    const tail = styles.map(id => STYLE_PRESETS.find(p => p.id === id)?.suffix).filter(Boolean).join('، ');
    return tail ? `${base}، ${tail}` : base;
  };

  const gen = async () => {
    if (!prompt.trim() || busy) return;
    cancelRef.current = false;
    setBusy(true); setErr(''); setImg(''); setPhase('ارسال به صف...'); setElapsed(0);
    try {
      const r = await axios.post('/admin/ai/generate-image', { prompt: fullPrompt(), ratio });
      if (r.data.path) { setImg(r.data.path); setBusy(false); setPhase(''); return; } // direct fallback
      const jid = r.data.job_id;
      if (!jid) throw new Error('job');
      if (r.data.queue_position > 1) setPhase(`نفر ${r.data.queue_position} در صف...`);
      const s = await pollAiJob(jid, {
        isCancelled: () => cancelRef.current,
        onPhase: (st, ms) => {
          setElapsed(ms);
          const mins = Math.floor(ms / 60000);
          const tail = mins > 0 ? ` (${mins} دقیقه)` : '';
          setPhase(st === 'retry' ? 'قطعی لحظه‌ای؛ تلاش مجدد...' : st === 'running' ? `در حال ساخت...${tail}` : `در صف ساخت...${tail}`);
        },
      });
      if (s.status === 'done' && s.path) { setImg(s.path); setBusy(false); setPhase(''); return; }
      throw new Error('خروجی تصویر دریافت نشد؛ دوباره تلاش کن.');
    } catch (e) {
      if (!e.cancelled) setErr(e.message || 'خطا در ساخت تصویر.');
      setPhase('');
    }
    setBusy(false);
  };

  return (
    <>
      <MBtn variant="tonal" className="!border-green-200 !bg-green-50 !text-green-800 dark:!border-green-800 dark:!bg-green-950/40 dark:!text-green-300"
        onClick={() => { setOpen(true); setImg(''); setErr(''); setRatio(validSug || '1:1'); }}>
        <Sparkles size={15} /> هوش مصنوعی
      </MBtn>
      {open && (
        <div onClick={() => !busy && setOpen(false)} role="dialog" aria-modal="true"
          className="fixed inset-0 z-[1300] flex items-center justify-center overflow-y-auto bg-black/55 p-4 backdrop-blur-sm" dir="rtl">
          <div onClick={e => e.stopPropagation()}
            className="m-auto flex max-h-[90vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[20px] border border-border bg-card text-foreground shadow-2xl">
            <div className="shrink-0 border-b border-border bg-muted/50 px-5 pb-3.5 pt-4">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl bg-green-700 text-white"><Sparkles size={18} /></span>
                <span className="flex-1 text-[15px] font-extrabold">ساخت تصویر با هوش مصنوعی</span>
                <button type="button" onClick={() => { if (busy) cancelRef.current = true; else setOpen(false); }} aria-label="بستن"
                  className="inline-flex size-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition hover:bg-accent hover:text-foreground">
                  <X size={15} />
                </button>
              </div>
              <p className="mb-0 mt-2 text-[12px] leading-7 text-muted-foreground">به فارسی بنویس چه عکسی می‌خواهی؛ مثلاً «ساچمه‌های نقره براق روی پارچه مخمل مشکی». سبک‌ها خودکار به پرامپت اضافه می‌شوند.</p>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <label className="mb-1.5 block text-xs font-bold">توضیح تصویر</label>
              <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3} placeholder="..."
                className="w-full resize-y rounded-xl border border-input bg-field px-3.5 py-2.5 text-[13px] leading-7 text-foreground outline-none transition placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20" />
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-bold">سبک:</span>
                {STYLE_PRESETS.map(p => (
                  <button key={p.id} type="button" onClick={() => toggleStyle(p.id)}
                    className={cn('rounded-full border px-3 py-1.5 text-[12px] font-bold transition',
                      styles.includes(p.id)
                        ? 'border-green-600 bg-green-50 text-green-800 dark:border-green-500 dark:bg-green-950/40 dark:text-green-300'
                        : 'border-border bg-card text-muted-foreground hover:text-foreground')}>
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-bold">ابعاد:</span>
                {['1:1', '16:9', '9:16', '4:3', '3:4'].map(r => (
                  <button key={r} type="button" onClick={() => setRatio(r)} dir="ltr" title={RATIO_LABELS[r]}
                    className={cn('rounded-lg border px-3 py-1.5 text-[12px] font-bold transition tabular-nums',
                      ratio === r ? 'border-2 border-foreground bg-muted' : 'border-border bg-card text-muted-foreground hover:text-foreground')}>
                    {r}{r === validSug ? ' ★' : ''}
                  </button>
                ))}
              </div>
              {validSug && <p className="mb-0 mt-2 text-[12px] font-bold text-green-700 dark:text-green-400">پیشنهاد این بخش: {RATIO_LABELS[validSug]} ({validSug})</p>}
              {err && <div className="mt-3 rounded-xl bg-red-50 p-2.5 text-[13px] font-semibold leading-7 text-red-800 dark:bg-red-950/40 dark:text-red-300">{err}</div>}
              {img && (
                <div className="mt-3 overflow-hidden rounded-2xl border border-border">
                  <img src={resolveSrc ? resolveSrc(img) : img} alt="خروجی هوش مصنوعی" className="block max-h-80 w-full bg-black object-contain" />
                </div>
              )}
            </div>
            <div className="flex shrink-0 gap-2.5 border-t border-border bg-muted/50 p-3.5 px-5">
              <MBtn className="flex-1 !bg-green-700" onClick={gen} disabled={busy || !prompt.trim()}>
                {busy ? <LoaderCircle size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {busy ? (phase || 'در حال ساخت...') : 'ساخت تصویر'}
              </MBtn>
              {img && (
                <MBtn className="flex-1" onClick={() => { onPick(img); setOpen(false); }}>
                  <Check size={16} /> استفاده از این تصویر
                </MBtn>
              )}
            </div>
            {busy && elapsed > 0 && (
              <div className="shrink-0 border-t border-border px-5 py-2 text-center text-[11px] tabular-nums text-muted-foreground">
                {fmtElapsed(elapsed)} گذشته — می‌توانی پنجره را ببندی؛ با «لغو» (×) ساخت متوقف می‌شود.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function AiImageInline(props) {
  return (
    <div className="flex items-center gap-2">
      <ImagePlus size={15} className="shrink-0 text-muted-foreground" />
      <AiImageButton {...props} />
    </div>
  );
}
